import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { FormattedMessage, useIntl } from "react-intl";
import { useCallback, useEffect, useState } from "react";
import {
  MAX_VOCABULARIES_PER_MEETING,
  Vocabulary,
  VocabularyError,
  VocabularySummary,
  VocabularyTerm,
  deleteVocabulary,
  getVocabulary,
  isValidVocabularyName,
  listVocabularies,
  putVocabulary,
  setDefaultVocabularies,
} from "../../lib/vocabularies";
import { useCandyVoiceLanguages } from "../../lib/candyvoice";
import { LanguageSelect } from "../Room/LanguageSelect";
import { ReactComponent as Trash } from "../../assets/Trash.svg";
import { ReactComponent as DeleteSvg } from "../../assets/deleteMemberSvg.svg";
import dialogStyles from "../../assets/stylesheets/dialog.module.scss";

interface Props {
  organizationId: string;
  // its plan lets it make vocabularies of its own: Pro Security and above, see
  // Meetingrooms.Plans in the backend. Otherwise it only has the common ones,
  // and can delete its own.
  customAllowed: boolean;
}

// The Vocabularies tab of the organization page, for its owners and admins:
// the vocabularies the organization can use, the ones used by default in its
// meetings, and its own ones to create, edit and delete. See
// src/lib/vocabularies.ts
export const Vocabularies: React.FC<Props> = ({
  organizationId,
  customAllowed,
}) => {
  const [vocabularies, setVocabularies] = useState<VocabularySummary[] | null>(
    null
  );
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState<VocabularyError | null>(null);
  // the vocabulary being edited, a new one when its name is empty
  const [editing, setEditing] = useState<Vocabulary | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(() => {
    listVocabularies(organizationId).then(
      (list) => {
        setVocabularies(list);
        setLoadError(false);
      },
      () => setLoadError(true)
    );
  }, [organizationId]);

  useEffect(() => {
    setVocabularies(null);
    setEditing(null);
    load();
  }, [load]);

  if (loadError) {
    return (
      <Typography color="error">
        <FormattedMessage
          id="vocabularies.load-error"
          defaultMessage="Unable to load the vocabularies."
        />
      </Typography>
    );
  }
  if (vocabularies === null) return <CircularProgress />;

  if (editing) {
    return (
      <VocabularyEditor
        organizationId={organizationId}
        vocabulary={editing}
        existingNames={vocabularies.map((v) => v.name)}
        onDone={(saved) => {
          setEditing(null);
          if (saved) load();
        }}
      />
    );
  }

  const defaults = vocabularies.filter((v) => v.default).map((v) => v.name);

  const toggleDefault = (name: string) => {
    const names = defaults.includes(name)
      ? defaults.filter((n) => n !== name)
      : [...defaults, name];
    setError(null);
    setDefaultVocabularies(organizationId, names).then(load, setError);
  };

  const edit = (name: string) => {
    setError(null);
    getVocabulary(organizationId, name).then(setEditing, setError);
  };

  const confirmDelete = () => {
    const name = deleting!;
    setDeleting(null);
    setError(null);
    deleteVocabulary(organizationId, name).then(load, setError);
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 900 }}
    >
      <Typography>
        <FormattedMessage
          id="vocabularies.sentence1"
          defaultMessage="The vocabularies write the acronyms and the terms of your organization right in the transcription of the voice translation, and keep them from being translated."
        />
      </Typography>
      <Typography>
        <FormattedMessage
          id="vocabularies.sentence2"
          defaultMessage="The ones checked are used by default in your meetings ({max} at most), the organizer of a meeting can choose others."
          values={{ max: MAX_VOCABULARIES_PER_MEETING }}
        />
      </Typography>
      {error && <VocabularyErrorMessage error={error} />}
      <Box>
        {customAllowed ? (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => {
              setError(null);
              setEditing({ name: "", language: "fr", terms: [{ term: "" }] });
            }}
          >
            <FormattedMessage
              id="vocabularies.new"
              defaultMessage="New vocabulary"
            />
          </Button>
        ) : (
          <Typography>
            <FormattedMessage
              id="vocabularies.pro-security-only"
              defaultMessage="Making vocabularies of your own is part of <a>Pro Security</a>."
              values={{
                a: (...chunks: React.ReactNode[]) => (
                  <a href="/pricing">{chunks}</a>
                ),
              }}
            />
          </Typography>
        )}
      </Box>
      <table className="table table-bordered table-hover">
        <thead>
          <tr>
            <th>
              <FormattedMessage id="apikeys.col-name" defaultMessage="Name" />
            </th>
            <th>
              <FormattedMessage
                id="vocabularies.col-terms"
                defaultMessage="Number of terms"
              />
            </th>
            <th>
              <FormattedMessage
                id="vocabularies.col-default"
                defaultMessage="Used by default"
              />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {vocabularies.map((vocabulary) => (
            <tr key={vocabulary.name}>
              <td>
                {/* the common ones of CandyVoice: CandyVoice doesn't give
                    their terms */}
                {vocabulary.common || !customAllowed ? (
                  vocabulary.name
                ) : (
                  <Link
                    component="button"
                    color="secondary"
                    onClick={() => edit(vocabulary.name)}
                  >
                    {vocabulary.name}
                  </Link>
                )}
                {vocabulary.common && <ReadOnlyChip />}
              </td>
              <td>{vocabulary.terms}</td>
              <td>
                <Checkbox
                  size="small"
                  color="secondary"
                  checked={vocabulary.default}
                  disabled={
                    !vocabulary.default &&
                    defaults.length >= MAX_VOCABULARIES_PER_MEETING
                  }
                  onChange={() => toggleDefault(vocabulary.name)}
                />
              </td>
              <td>
                {!vocabulary.common && (
                  <DeleteButton onClick={() => setDeleting(vocabulary.name)} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* like the deletion of an avatar, see DeleteAvatarButton.tsx */}
      <Modal open={deleting !== null} onClose={() => setDeleting(null)}>
        <Box
          className={dialogStyles.dialog}
          sx={{ minWidth: "350px", bgcolor: "background.paper" }}
        >
          <DeleteSvg />
          <Typography className={dialogStyles.title}>
            <FormattedMessage
              id="vocabularies.delete-title"
              defaultMessage="Delete the vocabulary {name}?"
              values={{ name: deleting }}
            />
          </Typography>
          <Typography className={dialogStyles.secondaryText}>
            <FormattedMessage
              id="vocabularies.delete-sentence"
              defaultMessage="The meetings won't use it anymore."
            />
          </Typography>
          <Box className={dialogStyles.actions}>
            <Button
              onClick={() => setDeleting(null)}
              className={dialogStyles.cancelButton}
              variant="contained"
              color="secondary"
            >
              <FormattedMessage id="actions.back" defaultMessage="Back" />
            </Button>
            <Button
              className={dialogStyles.validateButton}
              onClick={confirmDelete}
              variant="contained"
            >
              <FormattedMessage id="action-delete.yes" defaultMessage="Yes" />
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

// The trash of the other lists, see DeleteAvatarButton.tsx
const DeleteButton = ({
  onClick,
  title,
}: {
  onClick: () => void;
  title?: string;
}) => {
  const intl = useIntl();
  return (
    <Tooltip
      title={
        title ??
        intl.formatMessage({ id: "actions.delete", defaultMessage: "Delete" })
      }
    >
      <IconButton onClick={onClick}>
        <Trash />
      </IconButton>
    </Tooltip>
  );
};

const ReadOnlyChip = () => (
  <Chip
    size="small"
    sx={{ ml: 1 }}
    label={
      <FormattedMessage
        id="vocabularies.read-only"
        defaultMessage="Read only"
      />
    }
  />
);

const VocabularyErrorMessage = ({ error }: { error: VocabularyError }) => (
  <Alert severity="error">
    {error.error === "conflict" ? (
      <FormattedMessage
        id="vocabularies.error-conflict"
        defaultMessage="This name is taken by a vocabulary of CandyVoice, or the organization already has 20 vocabularies."
      />
    ) : error.error === "invalid" ? (
      <FormattedMessage
        id="vocabularies.error-invalid"
        defaultMessage="This vocabulary is not valid."
      />
    ) : error.error === "too_big" ? (
      <FormattedMessage
        id="vocabularies.error-too-big"
        defaultMessage="This vocabulary is too big."
      />
    ) : (
      <FormattedMessage
        id="vocabularies.error-failed"
        defaultMessage="It didn't work, please try again later."
      />
    )}
    {/* what CandyVoice said, in English */}
    {error.detail && (
      <Box sx={{ mt: 0.5, fontSize: "90%" }}>{error.detail}</Box>
    )}
  </Alert>
);

// A term while it is edited: its aliases as one text, separated by commas
interface EditedTerm {
  key: number;
  term: VocabularyTerm;
  aliases: string;
}

let nextKey = 0;
const toEdited = (term: VocabularyTerm): EditedTerm => ({
  key: nextKey++,
  term,
  aliases: (term.aliases ?? []).join(", "),
});

const fromEdited = ({ term, aliases }: EditedTerm): VocabularyTerm => {
  const { aliases: _, definition, ...others } = term;
  const list = aliases
    .split(",")
    .map((alias) => alias.trim())
    .filter(Boolean);
  return {
    ...others,
    term: term.term.trim(),
    ...(definition?.trim() && { definition: definition.trim() }),
    ...(list.length > 0 && { aliases: list }),
  };
};

interface EditorProps {
  organizationId: string;
  // a new one when its name is empty
  vocabulary: Vocabulary;
  existingNames: string[];
  onDone: (saved: boolean) => void;
}

const VocabularyEditor: React.FC<EditorProps> = ({
  organizationId,
  vocabulary,
  existingNames,
  onDone,
}) => {
  const intl = useIntl();
  const isNew = vocabulary.name === "";
  const [name, setName] = useState(vocabulary.name);
  const [language, setLanguage] = useState(vocabulary.language ?? "fr");
  const [terms, setTerms] = useState(() => vocabulary.terms.map(toEdited));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<VocabularyError | null>(null);
  const languages = useCandyVoiceLanguages();

  const nameError = isNew
    ? name && !isValidVocabularyName(name)
      ? intl.formatMessage({
          id: "vocabularies.name-invalid",
          defaultMessage:
            "Lowercase letters, digits, - and _ only, starting with a letter or a digit.",
        })
      : existingNames.includes(name)
      ? intl.formatMessage({
          id: "vocabularies.name-taken",
          defaultMessage: "This name is already taken.",
        })
      : null
    : null;
  const filled = terms.filter((t) => t.term.term.trim());
  const canSave =
    !saving && isValidVocabularyName(name) && !nameError && filled.length > 0;

  const update = (key: number, change: Partial<EditedTerm>) =>
    setTerms((terms) =>
      terms.map((t) => (t.key === key ? { ...t, ...change } : t))
    );
  const updateTerm = (key: number, change: Partial<VocabularyTerm>) =>
    setTerms((terms) =>
      terms.map((t) =>
        t.key === key ? { ...t, term: { ...t.term, ...change } } : t
      )
    );

  const save = () => {
    setSaving(true);
    setError(null);
    putVocabulary(organizationId, {
      name,
      language,
      terms: filled.map(fromEdited),
    }).then(
      () => onDone(true),
      (error) => {
        setSaving(false);
        setError(error);
      }
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
        maxWidth: 1100,
      }}
    >
      <Button
        onClick={() => onDone(false)}
        variant="outlined"
        color="secondary"
      >
        <FormattedMessage
          id="vocabularies.back"
          defaultMessage="Back to the vocabularies"
        />
      </Button>
      {isNew ? (
        <TextField
          label={intl.formatMessage({
            id: "apikeys.col-name",
            defaultMessage: "Name",
          })}
          value={name}
          onChange={(event) => setName(event.target.value.toLowerCase())}
          error={!!nameError}
          helperText={
            nameError ??
            intl.formatMessage({
              id: "vocabularies.name-help",
              defaultMessage: "For example acme-legal",
            })
          }
          size="small"
          inputProps={{ maxLength: 64 }}
        />
      ) : (
        <Typography variant="h6">{name}</Typography>
      )}
      <Box sx={{ minWidth: 300 }}>
        <label>
          <FormattedMessage
            id="vocabularies.language"
            defaultMessage="Language of the definitions"
          />
        </label>
        <LanguageSelect
          id="vocabularyLanguage"
          languages={languages.spoken}
          value={language}
          onChange={setLanguage}
          menuPlacement="bottom"
        />
      </Box>
      <Alert severity="info">
        <FormattedMessage
          id="vocabularies.terms-help"
          defaultMessage="The term is written exactly like this in the transcription. Its definition is added in brackets the first time it is said. The aliases are what the speech recognition writes instead of the term, separated by commas: you find them in the transcriptions of your meetings. Don't put the names of people in it."
        />
      </Alert>
      <table className="table table-bordered" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th style={{ width: "20%" }}>
              <FormattedMessage
                id="vocabularies.col-term"
                defaultMessage="Term"
              />
            </th>
            <th style={{ width: "45%" }}>
              <FormattedMessage
                id="vocabularies.col-definition"
                defaultMessage="Definition"
              />
            </th>
            <th style={{ width: "30%" }}>
              <FormattedMessage
                id="vocabularies.col-aliases"
                defaultMessage="Aliases"
              />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {terms.map((t) => (
            <tr key={t.key}>
              <td>
                <TextField
                  size="small"
                  fullWidth
                  value={t.term.term}
                  placeholder="PSIG"
                  onChange={(event) =>
                    updateTerm(t.key, { term: event.target.value })
                  }
                />
              </td>
              <td>
                <TextField
                  size="small"
                  fullWidth
                  value={t.term.definition ?? ""}
                  onChange={(event) =>
                    updateTerm(t.key, { definition: event.target.value })
                  }
                />
              </td>
              <td>
                <TextField
                  size="small"
                  fullWidth
                  value={t.aliases}
                  onChange={(event) =>
                    update(t.key, { aliases: event.target.value })
                  }
                />
              </td>
              <td>
                <DeleteButton
                  title={intl.formatMessage({
                    id: "vocabularies.remove-term",
                    defaultMessage: "Remove the term",
                  })}
                  onClick={() =>
                    setTerms((terms) => terms.filter((x) => x.key !== t.key))
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button
        color="secondary"
        startIcon={<FontAwesomeIcon icon={faPlus} />}
        onClick={() => setTerms((terms) => [...terms, toEdited({ term: "" })])}
      >
        <FormattedMessage
          id="vocabularies.add-term"
          defaultMessage="Add a term"
        />
      </Button>
      {error && <VocabularyErrorMessage error={error} />}
      {/* like the footer of the forms in a modal, see UpdateKeyButton.tsx */}
      <Box
        sx={{
          display: "flex",
          gap: "10px",
          alignSelf: "stretch",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={() => onDone(false)}
        >
          <FormattedMessage id="actions.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSave}
          onClick={save}
        >
          <FormattedMessage id="actions.save" defaultMessage="Save" />
        </Button>
      </Box>
    </Box>
  );
};
